import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "./ui/Dialog";
import { AlertTriangle, XCircle, Info, CheckCircle, X } from "lucide-react";
import { cn } from "../lib/utils";

interface ErrorDialogProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    message: string;
    type?: "error" | "warning" | "info" | "success";
    showRetry?: boolean;
    onRetry?: () => void;
    details?: string;
    actions?: {
        label: string;
        onClick: () => void;
        variant?: "primary" | "secondary" | "danger" | "success";
    }[];
    // New countdown props
    countdown?: number; // Duration in seconds
    showCountdown?: boolean; // Whether to show the countdown text
}

const ErrorDialog: React.FC<ErrorDialogProps> = ({
    isOpen,
    onClose,
    title,
    message,
    type = "error",
    showRetry = false,
    onRetry,
    details,
    actions = [],
    countdown,
    showCountdown = true,
}) => {
    const [timeLeft, setTimeLeft] = useState<number>(countdown || 0);

    useEffect(() => {
        // Reset countdown when dialog opens
        if (isOpen && countdown) {
            setTimeLeft(countdown);

            // Start countdown
            const timer = setInterval(() => {
                setTimeLeft((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        onClose();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            // Cleanup timer
            return () => clearInterval(timer);
        }
    }, [isOpen, countdown, onClose]);

    const config = {
        error: {
            icon: XCircle,
            iconColor: "text-red-500",
            iconBg: "bg-red-50",
            titleColor: "text-gray-900",
        },
        warning: {
            icon: AlertTriangle,
            iconColor: "text-amber-500",
            iconBg: "bg-amber-50",
            titleColor: "text-gray-900",
        },
        info: {
            icon: Info,
            iconColor: "text-blue-500",
            iconBg: "bg-blue-50",
            titleColor: "text-gray-900",
        },
        success: {
            icon: CheckCircle,
            iconColor: "text-green-500",
            iconBg: "bg-green-50",
            titleColor: "text-gray-900",
        },
    };

    const IconComponent = config[type].icon;

    const getButtonClasses = (
        variant: "primary" | "secondary" | "danger" | "success" = "primary"
    ) => {
        const baseClasses =
            "inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

        switch (variant) {
            case "primary":
                return cn(
                    baseClasses,
                    "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
                );
            case "secondary":
                return cn(
                    baseClasses,
                    "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-gray-500"
                );
            case "danger":
                return cn(
                    baseClasses,
                    "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
                );
            case "success":
                return cn(
                    baseClasses,
                    "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500"
                );
            default:
                return baseClasses;
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md p-0 bg-white overflow-hidden">
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-gray-100">
                    <X className="h-4 w-4 text-gray-500" />
                    <span className="sr-only">Close</span>
                </button>

                <div className="px-6 py-8">
                    <DialogHeader className="space-y-4">
                        <div className="flex items-start space-x-4">
                            <div
                                className={cn(
                                    "p-2 rounded-full",
                                    config[type].iconBg
                                )}>
                                <IconComponent
                                    className={cn(
                                        "h-6 w-6",
                                        config[type].iconColor
                                    )}
                                />
                            </div>
                            <div className="flex-1 space-y-2">
                                <DialogTitle
                                    className={cn(
                                        config[type].titleColor,
                                        "text-xl font-semibold tracking-tight"
                                    )}>
                                    {title ||
                                        type.charAt(0).toUpperCase() +
                                            type.slice(1)}
                                </DialogTitle>
                                <DialogDescription className="text-gray-500 text-base leading-relaxed">
                                    {message}
                                    {countdown &&
                                        showCountdown &&
                                        timeLeft > 0 && (
                                            <span className="block mt-2 text-sm text-gray-400">
                                                Closing in {timeLeft} seconds...
                                            </span>
                                        )}
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>

                    {details && (
                        <div className="mt-6">
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 overflow-x-auto">
                                <div className="text-xs font-mono text-gray-600 whitespace-pre-wrap">
                                    {details}
                                </div>
                            </div>
                        </div>
                    )}

                    <DialogFooter className="flex flex-col sm:flex-row gap-3 mt-8">
                        <div className="flex flex-col sm:flex-row gap-3 w-full justify-end">
                            {showRetry && onRetry && (
                                <button
                                    onClick={onRetry}
                                    className={getButtonClasses("primary")}>
                                    Retry
                                </button>
                            )}

                            {actions.map((action, index) => (
                                <button
                                    key={index}
                                    onClick={action.onClick}
                                    className={getButtonClasses(
                                        action.variant
                                    )}>
                                    {action.label}
                                </button>
                            ))}

                            {!actions.length && !showRetry && (
                                <button
                                    onClick={onClose}
                                    className={getButtonClasses("primary")}>
                                    Close
                                </button>
                            )}
                        </div>
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ErrorDialog;
